import { orgchart } from '@cwos/api-client';

/**
 * Pure derivations for the org chart.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE REDUCES `managers` TO ONE
 * ---------------------------------------------------------------------------
 * A person has an organizational manager at org scope AND a program supervisor
 * at program scope, at the same time, and neither is "the" manager. The API
 * returns an array for exactly that reason. Every helper in this file either
 * preserves the array or COUNTS it; none of them picks a head, sorts to find a
 * primary, or exposes a `parentId`. A single-parent shape anywhere in this
 * file — even as a convenience for a widget — is how the tree the data model
 * refuses gets rebuilt one accessor at a time, and the first thing it does is
 * drop somebody's real supervisor without saying so.
 */

/** How a manager edge reads on screen: who, in what capacity, over what. */
export interface ManagerLine {
  relationshipId: string;
  managerUserId: string;
  /** "Program supervisor", or the customer's own type name for a custom edge. */
  capacity: string;
  /** "Organization-wide" / "Program" / "Location" / "Team". */
  scope: string;
  /** Whether this edge routes work and confers authority. */
  authorityBearing: boolean;
  since: string;
  /** Non-null when the edge has an end date recorded. */
  until: string | null;
}

export function managerLine(edge: orgchart.RelationshipView): ManagerLine {
  return {
    relationshipId: edge.id,
    managerUserId: edge.managerUserId,
    capacity:
      edge.relationshipKind === 'custom'
        ? // The customer named this relationship; use their word, not ours.
          (edge.relationshipTypeName ?? edge.relationshipTypeKey ?? 'Custom relationship')
        : orgchart.RELATIONSHIP_KIND_LABEL[edge.relationshipKind],
    scope: orgchart.SCOPE_TYPE_LABEL[edge.scopeType],
    authorityBearing: edge.authorityBearing,
    since: edge.effectiveFrom,
    until: edge.effectiveTo,
  };
}

/**
 * The sentence above the manager list.
 *
 * It STATES THE COUNT, which is the point: "Two people manage Sarah" is a fact
 * a reader can check against the list underneath, and it is the sentence that
 * makes a missing second manager visible. A screen that renders only a list can
 * silently render a shorter one.
 */
export function managerCountSentence(name: string, count: number): string {
  if (count === 0) {
    return `No one currently manages ${name}. That is a gap in the chart, not a display problem — work routed to “their manager” has nowhere to go.`;
  }
  if (count === 1) {
    return `One person manages ${name} today.`;
  }
  return `${count} people manage ${name} today, in different capacities. None of them is the manager — this organisation does not have a single reporting line, and the chart does not pretend otherwise.`;
}

export function reportCountSentence(name: string, count: number): string {
  if (count === 0) return `No one reports to ${name} today.`;
  if (count === 1) return `One person reports to ${name} today.`;
  return `${count} people report to ${name} today.`;
}

/**
 * How long a coverage window has left, as words.
 *
 * Delegated authority is never written down — the server recomputes it from
 * `starts_at <= now() < ends_at` on every request — so a surface showing it must
 * show WHEN IT ENDS. "Covering for X" without "until Thursday 17:00" is the
 * half of the fact that makes an administrator think somebody needs to go and
 * revoke something.
 */
export function coverageWindowSentence(
  delegation: orgchart.DelegatedAuthority,
  coveredName: string,
  now: Date,
): string {
  const ends = new Date(delegation.endsAt);
  if (Number.isNaN(ends.getTime())) return `Covering for ${coveredName} — end time unreadable.`;
  if (ends.getTime() <= now.getTime()) {
    // The server would not have returned it, so seeing this means the page has
    // been open across the boundary. Say so rather than keep showing authority.
    return `Cover for ${coveredName} ended at ${formatWhen(ends)}. Refresh to see the current answer.`;
  }
  return `Covering for ${coveredName} until ${formatWhen(ends)}, when it ends on its own. Nothing has to be revoked.`;
}

/** Grants a delegate is borrowing, summarised without listing 40 keys. */
export function borrowedSummary(delegation: orgchart.DelegatedAuthority): string {
  const n = delegation.grants.length;
  if (n === 0) {
    return 'Borrowing nothing: the person being covered holds no grants right now.';
  }
  return `Borrowing ${n} permission${n === 1 ? '' : 's'}, capped at what that person holds right now — never more.`;
}

function formatWhen(when: Date): string {
  return when.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(iso: string): string {
  // Dates from this domain are calendar days (`effective_from`), not instants;
  // parsing them as UTC midnight and formatting in local time can move them a
  // day. Render the recorded day as recorded.
  return iso.length === 10 ? iso : iso.slice(0, 10);
}
